import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from '../assets/propertyViewings.module.css';
import propertyViewingsService from '../services/propertyViewingsService';
import PropertyViewingsCalendar from '../components/PropertyViewingsCalendar';
import PropertyViewingForm from '../components/PropertyViewingFormFinal';
import modalStyles from '../assets/propertyViewingsCalendar.module.css';

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const PropertyViewings = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [property, setProperty] = useState(null);
  const [viewings, setViewings] = useState([]);
  const [filteredViewings, setFilteredViewings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStatusFilter, setActiveStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [users, setUsers] = useState([]);

  // State for sorting
  const [sortField, setSortField] = useState("day");
  const [sortDirection, setSortDirection] = useState("asc");
  
  // Statistics - matching Laravel image exactly
  const [statistics, setStatistics] = useState({
    viewingsAll: 64, // "Viewing" in Laravel image
    viewingsUpcoming: 0,
    viewingsPast: 0, // "Missed" in Laravel image
    viewingsCompleted: 0,
    viewingsUncompleted: 0
  });


  // Load data
  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Real API call - backend returns all data in one call
      const response = await propertyViewingsService.getPropertyViewingsCalendar(id);
      const data = response.data.data; // Extract nested data structure
      
      setProperty(data.property);
      setViewings(data.viewings);
      setFilteredViewings(data.viewings); // Initialize filtered viewings
      setStatistics(data.statusOverview);
      
      // Load users for dropdown
      try {
        const usersResponse = await propertyViewingsService.getUsersForPropertyViewings();
        console.log('🔍 [USERS] Full response:', usersResponse);
        console.log('🔍 [USERS] Response data:', usersResponse.data);
        const usersData = usersResponse.data?.data || usersResponse.data || {};
        console.log('🔍 [USERS] Extracted users (object):', usersData);
        
        // Convert object format {1: "Name", 2: "Name"} to array format [{id: 1, full_name: "Name"}, ...]
        let usersArray = [];
        if (Array.isArray(usersData)) {
          // Already an array
          usersArray = usersData;
        } else if (typeof usersData === 'object' && usersData !== null) {
          // Convert object to array
          usersArray = Object.keys(usersData).map(id => {
            const name = usersData[id];
            // Parse name to extract first_name and last_name if needed
            const nameParts = name.split(' ');
            return {
              id: parseInt(id),
              full_name: name,
              first_name: nameParts[0] || '',
              last_name: nameParts.slice(1).join(' ') || ''
            };
          });
        }
        
        console.log('🔍 [USERS] Converted to array:', usersArray);
        console.log('🔍 [USERS] Users count:', usersArray.length);
        setUsers(usersArray);
      } catch (userError) {
        console.error('❌ [USERS] Error loading users:', userError);
        setUsers([]);
      }
      
      console.log('📊 [FRONTEND] Property viewings data loaded from database:', data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load property viewings data. Please try again later.');
      setProperty(null);
      setViewings([]);
      setFilteredViewings([]);
      setStatistics({
        viewingsAll: 0,
        viewingsUpcoming: 0,
        viewingsPast: 0,
        viewingsCompleted: 0,
        viewingsUncompleted: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle add viewing
  const handleAddViewing = () => {
    setShowAddModal(true);
  };

  // Handle form submission from PropertyViewingForm
  const handleSubmitViewing = async (formData) => {
    try {
      setLoading(true);
      
      // Create the viewing via API
      await propertyViewingsService.createPropertyViewing(formData);
      
      // Refresh the viewings list
      await loadData();
      setShowAddModal(false);
      toast.success('Property viewing created successfully');
      return true; // Return true to indicate success
    } catch (error) {
      console.error('Error submitting viewing:', error);
      toast.error('Failed to create viewing');
      return false; // Return false to indicate failure
    } finally {
      setLoading(false);
    }
  };

  // Handle status box click - filter viewings by status
  const handleStatusBoxClick = (statusId) => {
    setActiveStatusFilter(statusId);
    
    if (statusId === 'all') {
      setFilteredViewings(viewings);
    } else {
      const filtered = viewings.filter((viewing) => {
        switch (statusId) {
          case '0': // Upcoming
            return new Date(viewing.day) > new Date();
          case '1': // Past/Missed
            return new Date(viewing.day) < new Date();
          case '2': // Completed
            return viewing.confirmed === true;
          case '3': // Uncompleted
            return viewing.confirmed === false;
          default:
            return true;
        }
      });
      setFilteredViewings(filtered);
    }
    
    console.log(`🎯 [FRONTEND] Filtering by status: ${getStatusName(statusId)} - ${filteredViewings.length} viewings`);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Handle calendar event click
  const handleCalendarEventClick = (viewing) => {
    // In Laravel, this opens an edit modal
    // For now, navigate to edit page
    navigate(`/property_viewings/${viewing.id}/edit`);
  };

  // Handle edit viewing
  const handleEditViewing = (viewing) => {
    navigate(`/property_viewings/${viewing.id}/edit`);
  };

  // Handle delete viewing
  const handleDeleteViewing = async (viewing) => {
    if (window.confirm(`Are you sure you want to delete the viewing for ${viewing.first_name} ${viewing.last_name}?`)) {
      try {
        await propertyViewingsService.deletePropertyViewing(viewing.id);
        toast.success('Viewing deleted successfully');
        // Reload data
        loadData();
      } catch (error) {
        console.error('Error deleting viewing:', error);
        toast.error('Failed to delete viewing. Please try again.');
      }
    }
  };

  // Handle export functions (like Laravel DataTables buttons)
  const handleExportCSV = () => {
    if (viewings.length === 0) {
      toast.warning('No data to export');
      return;
    }

    // Create CSV content
    const headers = ['First Name', 'Last Name', 'Day', 'Time From', 'Time To', 'Confirmed', 'Attendees', 'Rooms Taken'];
    const csvContent = [
      headers.join(','),
      ...viewings.map(viewing => [
        viewing.first_name || '',
        viewing.last_name || '',
        viewing.day ? new Date(viewing.day).toLocaleDateString('en-GB') : '',
        viewing.time_from || '',
        viewing.time_to || '',
        viewing.confirmed || 0,
        viewing.attendees || '',
        viewing.rooms_taken || ''
      ].join(','))
    ].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `property_viewings_${property?.name || 'property'}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('CSV exported successfully');
  };

  const handleExportPDF = () => {
    if (viewings.length === 0) {
      toast.warning('No data to export');
      return;
    }

    // Create PDF content using jsPDF
    const { jsPDF } = require('jspdf');
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(16);
    doc.text(`Property Viewings - ${property?.name || 'Property'}`, 20, 20);
    
    // Add date
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
    
    // Add table headers
    const headers = ['First', 'Last', 'Day', 'From', 'To', 'Confirmed', 'Attendees', 'Taken'];
    let yPosition = 50;
    
    doc.setFontSize(10);
    headers.forEach((header, index) => {
      doc.text(header, 20 + (index * 25), yPosition);
    });
    
    // Add data rows
    yPosition += 10;
    viewings.forEach((viewing, index) => {
      if (yPosition > 280) {
        doc.addPage();
        yPosition = 20;
      }
      
      const row = [
        String(viewing.first_name || ''),
        String(viewing.last_name || ''),
        viewing.day ? new Date(viewing.day).toLocaleDateString('en-GB') : '',
        String(viewing.time_from || ''),
        String(viewing.time_to || ''),
        String(viewing.confirmed || 0),
        String(viewing.attendees || ''),
        String(viewing.rooms_taken || '')
      ];
      
      row.forEach((cell, cellIndex) => {
        doc.text(String(cell), 20 + (cellIndex * 25), yPosition);
      });
      
      yPosition += 7;
    });
    
    // Save PDF
    doc.save(`property_viewings_${property?.name || 'property'}_${new Date().toISOString().split('T')[0]}.pdf`);
    
    toast.success('PDF exported successfully');
  };

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    firstName: true,
    lastName: true,
    day: true,
    timeFrom: true,
    timeTo: true,
    confirmed: true,
    attendees: true,
    roomsTaken: true,
    calendar: true,
    actions: true
  });

  const [showColumnVisibility, setShowColumnVisibility] = useState(false);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnVisibility && !event.target.closest(`.${styles.columnVisibilityContainer}`)) {
        setShowColumnVisibility(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnVisibility]);

  // Handle sorting and filtering
  useEffect(() => {
    let filtered = [...viewings];

    // Apply status filter
    if (activeStatusFilter !== 'all') {
      filtered = filtered.filter((viewing) => {
        switch (activeStatusFilter) {
          case '0': // Upcoming
            return new Date(viewing.day) > new Date();
          case '1': // Past/Missed
            return new Date(viewing.day) < new Date();
          case '2': // Completed
            return viewing.confirmed === true;
          case '3': // Uncompleted
            return viewing.confirmed === false;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested object sorting
      if (sortField === "firstName") {
        aValue = a.first_name || "";
        bValue = b.first_name || "";
      } else if (sortField === "lastName") {
        aValue = a.last_name || "";
        bValue = b.last_name || "";
      } else if (sortField === "timeFrom") {
        aValue = a.time_from || "";
        bValue = b.time_from || "";
      } else if (sortField === "timeTo") {
        aValue = a.time_to || "";
        bValue = b.time_to || "";
      } else if (sortField === "attendees") {
        aValue = a.attendees || 0;
        bValue = b.attendees || 0;
      } else if (sortField === "roomsTaken") {
        aValue = a.rooms_taken || 0;
        bValue = b.rooms_taken || 0;
      }

      // Handle date sorting
      if (sortField === "day") {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredViewings(filtered);
  }, [viewings, activeStatusFilter, sortField, sortDirection]);

  const handleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const columnOptions = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'day', label: 'Day' },
    { key: 'timeFrom', label: 'Time From' },
    { key: 'timeTo', label: 'Time To' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'attendees', label: 'Attendees' },
    { key: 'roomsTaken', label: 'Rooms Taken' },
    { key: 'calendar', label: 'Calendar' },
    { key: 'actions', label: 'Actions' }
  ];

  // Get status color - matching Laravel image exactly
  const getStatusColor = (statusId) => {
    const colors = {
      'all': '#337ab7', // Blue for "Viewing" (64)
      '0': '#5cb85c', // Green for "Upcoming" (0)
      '1': '#d9534f', // Red for "Missed" (0) 
      '2': '#f0ad4e', // Orange for "Completed" (0)
      '3': '#5bc0de' // Light blue for "Uncompleted" (0)
    };
    return colors[statusId] || '#337ab7';
  };

  // Get status name - matching Laravel image exactly
  const getStatusName = (statusId) => {
    const names = {
      'all': 'Viewing', // "Viewing" in Laravel image
      '0': 'Upcoming',
      '1': 'Missed', // "Missed" in Laravel image
      '2': 'Completed',
      '3': 'Uncompleted'
    };
    return names[statusId] || 'Viewing';
  };

  // Breadcrumbs
  const breadcrumbs = [
    { name: 'Dashboard', path: '/' },
    { name: 'Property Viewings', path: '/property_viewings' },
    { name: property?.name || 'Property', path: `/properties/${id}` },
    { name: 'Calendar', path: `/properties/${id}/viewings` }
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          Loading property viewings...
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>

      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        {breadcrumbs.map((crumb, index) => (
          <span key={index}>
            {index > 0 && <span className={styles.breadcrumbSeparator}>/</span>}
            {index === breadcrumbs.length - 1 ? (
              <span className={styles.breadcrumbItem}>{crumb.name}</span>
            ) : (
              <span
                className={styles.breadcrumbItem}
                onClick={() => navigate(crumb.path)}
                style={{ cursor: 'pointer', color: '#337ab7' }}
              >
                {crumb.name}
              </span>
            )}
          </span>
        ))}
      </div>

      {/* Main Panel */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h3 className={styles.panelTitle} style={{ margin: 0 }}>
                  Property Viewings of <strong>{property?.name}</strong>
                </h3>
                <button
                  className="btn btn-secondary"
                  onClick={handleAddViewing}
                  style={{ 
                    background: '#6c757d', 
                    color: 'white', 
                    border: 'none', 
                    padding: '8px 16px', 
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Add Viewing
                </button>
              </div>
            </div>
            <div className={styles.panelBody}>
              <PropertyViewingsCalendar
                propertyId={id}
                viewings={viewings}
                onEventClick={handleCalendarEventClick}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Status Overview */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panelTitle}>
            <h3 className={styles.titleStandard}>Status overview</h3>
          </div>
          <div className={`${styles.panelGroup} ${styles.mb20} ${styles.borderDown}`}>
            <div className={styles.row}>
              <div className={styles.colMd2}>
                <div 
                  className={`${styles.activityBox} ${activeStatusFilter === 'all' ? styles.activityBoxActive : ''}`}
                  style={{ backgroundColor: getStatusColor('all') }}
                  onClick={() => handleStatusBoxClick('all')}
                >
                  <p className={styles.activityValue}>{statistics.viewingsAll}</p>
                  <p className={styles.activityTitle}>Viewing</p>
                </div>
              </div>
              <div className={styles.colMd2}>
                <div 
                  className={`${styles.activityBox} ${activeStatusFilter === '0' ? styles.activityBoxActive : ''}`}
                  style={{ backgroundColor: getStatusColor('0') }}
                  onClick={() => handleStatusBoxClick('0')}
                >
                  <p className={styles.activityValue}>{statistics.viewingsUpcoming}</p>
                  <p className={styles.activityTitle}>Upcoming</p>
                </div>
              </div>
              <div className={styles.colMd2}>
                <div 
                  className={`${styles.activityBox} ${activeStatusFilter === '1' ? styles.activityBoxActive : ''}`}
                  style={{ backgroundColor: getStatusColor('1') }}
                  onClick={() => handleStatusBoxClick('1')}
                >
                  <p className={styles.activityValue}>{statistics.viewingsPast}</p>
                  <p className={styles.activityTitle}>Missed</p>
                </div>
              </div>
              <div className={styles.colMd2}>
                <div 
                  className={`${styles.activityBox} ${activeStatusFilter === '2' ? styles.activityBoxActive : ''}`}
                  style={{ backgroundColor: getStatusColor('2') }}
                  onClick={() => handleStatusBoxClick('2')}
                >
                  <p className={styles.activityValue}>{statistics.viewingsCompleted}</p>
                  <p className={styles.activityTitle}>Completed</p>
                </div>
              </div>
              <div className={styles.colMd2}>
                <div 
                  className={`${styles.activityBox} ${activeStatusFilter === '3' ? styles.activityBoxActive : ''}`}
                  style={{ backgroundColor: getStatusColor('3') }}
                  onClick={() => handleStatusBoxClick('3')}
                >
                  <p className={styles.activityValue}>{statistics.viewingsUncompleted}</p>
                  <p className={styles.activityTitle}>Uncompleted</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Viewings List */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3 className={styles.panelTitle}>List</h3>
            </div>
            <div className={styles.panelBody}>
              {filteredViewings.length > 0 ? (
                <div className={styles.dataTableWrapper}>
                  {/* DataTable Controls - matching Laravel's dt-toolbar */}
                  <div className={`${styles.dtToolbar} ${styles.dtToolbarNoButtons}`}>
                    <div className={styles.entriesControl}>
                      <span>Show</span>
                      <select className={styles.entriesSelect} defaultValue={30}>
                        <option value={30}>30</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={75}>75</option>
                        <option value={100}>100</option>
                        <option value={-1}>All</option>
                      </select>
                      <span>entries</span>
                    </div>

                    <div className={styles.searchControl}>
                      <span>Search:</span>
                      <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search..."
                      />
                    </div>

                    {/* Export Buttons - matching Laravel DataTables buttons */}
                    <div className={styles.exportButtons}>
                      <button
                        className={styles.exportButton}
                        onClick={handleExportCSV}
                      >
                        Export as CSV
                      </button>
                      <button
                        className={styles.exportButton}
                        onClick={handleExportPDF}
                      >
                        Export as PDF
                      </button>
                      <button
                        className={styles.exportButton}
                        onClick={() => toast.info('Export availability feature coming soon')}
                      >
                        Export availability
                      </button>
                      <div className={styles.columnVisibilityContainer}>
                        <button
                          className={styles.exportButton}
                          onClick={handleColumnVisibility}
                        >
                          Column Visibility
                        </button>
                        {showColumnVisibility && (
                          <div className={styles.columnVisibilityDropdown}>
                            {columnOptions.map(option => (
                              <div key={option.key} className={styles.columnVisibilityItem}>
                                <label className={styles.columnVisibilityLabel}>
                                  <input
                                    type="checkbox"
                                    checked={visibleColumns[option.key]}
                                    onChange={() => toggleColumn(option.key)}
                                    className={styles.columnVisibilityCheckbox}
                                  />
                                  <span className={styles.columnVisibilityText}>
                                    {option.label}
                                  </span>
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <div className={styles.dtResponsive}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          {visibleColumns.firstName && (
                            <th 
                              className="sortable"
                              onClick={() => handleSort("firstName")}
                              style={{ cursor: "pointer" }}
                            >
                              First<SortIcon direction={sortField === "firstName" ? sortDirection : null} isActive={sortField === "firstName"} />
                            </th>
                          )}
                          {visibleColumns.lastName && (
                            <th 
                              className="sortable"
                              onClick={() => handleSort("lastName")}
                              style={{ cursor: "pointer" }}
                            >
                              Last<SortIcon direction={sortField === "lastName" ? sortDirection : null} isActive={sortField === "lastName"} />
                            </th>
                          )}
                          {visibleColumns.day && (
                            <th 
                              className="sortable"
                              onClick={() => handleSort("day")}
                              style={{ cursor: "pointer" }}
                            >
                              Day<SortIcon direction={sortField === "day" ? sortDirection : null} isActive={sortField === "day"} />
                            </th>
                          )}
                          {visibleColumns.timeFrom && (
                            <th 
                              className={`sortable ${styles.jsTooltip}`}
                              title="Time From"
                              onClick={() => handleSort("timeFrom")}
                              style={{ cursor: "pointer" }}
                            >
                              From<SortIcon direction={sortField === "timeFrom" ? sortDirection : null} isActive={sortField === "timeFrom"} />
                            </th>
                          )}
                          {visibleColumns.timeTo && (
                            <th 
                              className={`sortable ${styles.jsTooltip}`}
                              title="Time To"
                              onClick={() => handleSort("timeTo")}
                              style={{ cursor: "pointer" }}
                            >
                              To<SortIcon direction={sortField === "timeTo" ? sortDirection : null} isActive={sortField === "timeTo"} />
                            </th>
                          )}
                          {visibleColumns.confirmed && (
                            <th 
                              className="sortable"
                              onClick={() => handleSort("confirmed")}
                              style={{ cursor: "pointer" }}
                            >
                              Confirmed<SortIcon direction={sortField === "confirmed" ? sortDirection : null} isActive={sortField === "confirmed"} />
                            </th>
                          )}
                          {visibleColumns.attendees && (
                            <th 
                              className="sortable"
                              onClick={() => handleSort("attendees")}
                              style={{ cursor: "pointer" }}
                            >
                              Attendees<SortIcon direction={sortField === "attendees" ? sortDirection : null} isActive={sortField === "attendees"} />
                            </th>
                          )}
                          {visibleColumns.roomsTaken && (
                            <th 
                              className={`sortable ${styles.jsTooltip}`}
                              title="Rooms Taken"
                              onClick={() => handleSort("roomsTaken")}
                              style={{ cursor: "pointer" }}
                            >
                              Taken<SortIcon direction={sortField === "roomsTaken" ? sortDirection : null} isActive={sortField === "roomsTaken"} />
                            </th>
                          )}
                          {visibleColumns.calendar && <th>Calendar</th>}
                          {visibleColumns.actions && <th className={styles.actionHead}>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredViewings.map(viewing => (
                          <tr key={viewing.id}>
                            {visibleColumns.firstName && <td>{viewing.first_name}</td>}
                            {visibleColumns.lastName && <td>{viewing.last_name}</td>}
                            {visibleColumns.day && <td>{viewing.day ? new Date(viewing.day).toLocaleDateString('en-GB') : ''}</td>}
                            {visibleColumns.timeFrom && <td>{viewing.time_from}</td>}
                            {visibleColumns.timeTo && <td>{viewing.time_to}</td>}
                            {visibleColumns.confirmed && (
                              <td style={{ textAlign: 'center' }}>
                                <span
                                  onClick={() => handleEditViewing(viewing)}
                                  style={{ cursor: 'pointer', fontWeight: 'bold', color: '#337ab7' }}
                                  title="Click to edit viewing"
                                >
                                  {viewing.confirmed || 0}
                                </span>
                              </td>
                            )}
                            {visibleColumns.attendees && (
                              <td style={{ textAlign: 'center' }}>
                                <span
                                  onClick={() => handleEditViewing(viewing)}
                                  style={{ cursor: 'pointer', fontWeight: 'bold', color: '#337ab7' }}
                                  title="Click to edit viewing"
                                >
                                  {viewing.attendees}
                                </span>
                              </td>
                            )}
                            {visibleColumns.roomsTaken && <td>{viewing.rooms_taken}</td>}
                            {visibleColumns.calendar && (
                              <td>
                                <button 
                                  className={`${styles.btn} ${styles.btnInfo}`}
                                  onClick={() => navigate(`/property_viewings/user/${viewing.user_id}`)}
                                  style={{ fontSize: '11px', padding: '2px 6px' }}
                                >
                                  User
                                </button>
                              </td>
                            )}
                            {visibleColumns.actions && (
                              <td>
                                <div className={styles.actionButtons}>
                                  <button 
                                    className={`${styles.actionButton} ${styles.actionButtonInfo}`}
                                    onClick={() => handleEditViewing(viewing)}
                                    title="Edit viewing"
                                  >
                                    Edit
                                  </button>
                                  <button 
                                    className={`${styles.actionButton} ${styles.actionButtonDanger}`}
                                    onClick={() => handleDeleteViewing(viewing)}
                                    title="Delete viewing"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination - matching Laravel's row col-sm-5/col-sm-7 structure */}
                  <div className={styles.row}>
                    <div className={styles.colSm5}>
                      <div className={styles.paginationInfo}>
                        Showing 1 to {filteredViewings.length} of {filteredViewings.length} entries
                      </div>
                    </div>
                    <div className={styles.colSm7}>
                      <div className={styles.paginationControls}>
                        <button className={styles.paginationButton} disabled>
                          Previous
                        </button>
                        <button className={`${styles.paginationButton} ${styles.active}`}>
                          1
                        </button>
                        <button className={styles.paginationButton} disabled>
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.noData}>No viewings!</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Modal - Using PropertyViewingFormFinal to match Laravel */}
      {showAddModal && (
        <div className={`${modalStyles.modal} ${modalStyles.show}`}>
          <div className={modalStyles.modalDialog}>
            <div className={modalStyles.modalContent}>
              <div className={modalStyles.modalHeader}>
                <h4 className={modalStyles.modalTitle}>Add property viewing</h4>
                <button
                  className={modalStyles.btnModalClose}
                  onClick={() => setShowAddModal(false)}
                >
                  &times;
                </button>
              </div>
              <div className={modalStyles.modalBody}>
                {console.log('🔍 [MODAL] Users being passed to form:', users)}
                {console.log('🔍 [MODAL] Users array length:', users?.length)}
                <PropertyViewingForm
                  user={null} // User will be selected in the form
                  users={users} // Pass users array for dropdown
                  viewing={{
                    property_id: id,
                    property_title: property?.name || '',
                    postcode: property?.postcode || '',
                    user_id: '' // Empty user_id so dropdown shows "Please select"
                  }}
                  onSubmit={handleSubmitViewing}
                  isEdit={false}
                />
              </div>
              <div className={modalStyles.modalFooter} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px' }}>
                <button
                  className={`${modalStyles.btn} ${modalStyles.btnPrimary}`}
                  onClick={async () => {
                    // Trigger form submission via window function
                    if (window.propertyViewingFormSubmit) {
                      const result = window.propertyViewingFormSubmit();
                      // If validation failed, keep modal open
                      if (result === false) {
                        return;
                      }
                      // If validation passed, onSubmit will be called which handles the async submission
                      // The modal will be closed by handleSubmitViewing on success
                    }
                  }}
                  style={{ 
                    background: '#007bff', 
                    color: 'white', 
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Create
                </button>
                <button
                  className={`${modalStyles.btn} ${modalStyles.btnDefault}`}
                  onClick={() => setShowAddModal(false)}
                  style={{ 
                    background: '#d9534f', 
                    color: 'white', 
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyViewings; 